import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import './accounting.css';

const CostCenters = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    fetchCostCenters();
    fetchStatistics();
  }, []);

  const fetchCostCenters = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/accounting/cost-centers', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setCostCenters(data.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/accounting/cost-centers/statistics', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setStatistics(data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleToggleStatus = async (centerId) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/accounting/cost-centers/${centerId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      fetchCostCenters();
      fetchStatistics();
    } catch (err) {
      setError(err.message || 'حدث خطأ في تغيير الحالة');
    }
  };

  const handleSetDefault = async (centerId) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/accounting/cost-centers/${centerId}/set-default`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      fetchCostCenters();
      fetchStatistics();
    } catch (err) {
      setError(err.message || 'حدث خطأ في تعيين المركز كافتراضي');
    }
  };

  const handleDelete = async (centerId) => {
    if (window.confirm('هل أنت متأكد من حذف مركز التكلفة هذا؟')) {
      try {
        const response = await fetch(`https://naql.nozzm.com/api/accounting/cost-centers/${centerId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'lang': i18n.language,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        fetchCostCenters();
        fetchStatistics();
      } catch (err) {
        setError(err.message || 'حدث خطأ في حذف مركز التكلفة');
      }
    }
  };

  const filteredCenters = costCenters.filter(center => {
    const matchesSearch = center.center_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         center.center_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (center.description && center.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = !typeFilter || center.type === typeFilter;
    const matchesStatus = !statusFilter || center.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-success',
      inactive: 'bg-secondary',
      closed: 'bg-danger'
    };
    return (
      <span className={`badge ${statusClasses[status] || 'bg-secondary'}`}>
        {t(`accounting.costCenters.centerStatus.${status}`)}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    return (
      <span className="badge bg-info">
        {t(`accounting.costCenters.centerTypes.${type}`)}
      </span>
    );
  };

  const getBudgetStatus = (center) => {
    if (center.actual_amount > center.budget_amount) {
      return <span className="badge bg-danger">تجاوز الميزانية</span>;
    } else if (center.actual_amount > center.budget_amount * 0.8) {
      return <span className="badge bg-warning">قريب من الميزانية</span>;
    } else {
      return <span className="badge bg-success">ضمن الميزانية</span>;
    }
  };

  if (error) {
    return (
      <div className="cost-centers-container">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="cost-centers-container">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cost-centers-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h2>{t('accounting.costCenters.title')}</h2>
          <p>إدارة مراكز التكلفة والميزانيات</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/cost-centers/add')}
          >
            {t('accounting.costCenters.actions.add')}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="statistics-cards">
        <div className="stat-card">
          <div className="stat-icon">🏢</div>
          <div className="stat-content">
                         <h3>{statistics?.total_centers || 0}</h3>
            <p>{t('accounting.costCenters.statistics.totalCenters')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
                         <h3>{statistics?.active_centers || 0}</h3>
            <p>{t('accounting.costCenters.statistics.activeCenters')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
                         <h3>{statistics?.total_budget ? `$${(statistics.total_budget / 1000).toFixed(1)}K` : '$0'}</h3>
            <p>{t('accounting.costCenters.statistics.totalBudget')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
                         <h3>{statistics?.over_budget_centers || 0}</h3>
            <p>{t('accounting.costCenters.statistics.overBudgetCenters')}</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="row">
          <div className="col-md-4">
            <div className="search-box">
              <input
                type="text"
                className="form-control"
                placeholder="البحث في مراكز التكلفة..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">جميع الأنواع</option>
              <option value="department">قسم</option>
              <option value="project">مشروع</option>
              <option value="branch">فرع</option>
              <option value="product">منتج</option>
              <option value="service">خدمة</option>
              <option value="other">أخرى</option>
            </select>
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">جميع الحالات</option>
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
              <option value="closed">مغلق</option>
            </select>
          </div>
          <div className="col-md-2">
            <button 
              className="btn btn-outline-secondary w-100"
              onClick={() => {
                setSearchTerm('');
                setTypeFilter('');
                setStatusFilter('');
              }}
            >
              مسح الفلاتر
            </button>
          </div>
          <div className="col-md-2">
            <button 
              className="btn btn-outline-primary w-100"
              onClick={() => navigate('/cost-centers/hierarchy')}
            >
              عرض الهيكل
            </button>
          </div>
        </div>
      </div>

      {/* Cost Centers Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>{t('accounting.costCenters.centerCode')}</th>
                <th>{t('accounting.costCenters.centerName')}</th>
                <th>{t('accounting.costCenters.type')}</th>
                <th>{t('accounting.costCenters.parent')}</th>
                <th>{t('accounting.costCenters.budgetAmount')}</th>
                <th>{t('accounting.costCenters.actualAmount')}</th>
                <th>{t('accounting.costCenters.status')}</th>
                <th>حالة الميزانية</th>
                <th>{t('accounting.costCenters.actions.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredCenters.map((center) => (
                <tr key={center.id}>
                  <td>
                    <strong>{center.center_code}</strong>
                    {center.is_default && <span className="badge bg-primary ms-1">افتراضي</span>}
                  </td>
                  <td>
                    <div>
                      <strong>{center.center_name}</strong>
                      {center.description && (
                        <small className="text-muted d-block">{center.description}</small>
                      )}
                    </div>
                  </td>
                  <td>{getTypeBadge(center.type)}</td>
                  <td>{center.parent ? center.parent.center_name : '-'}</td>
                  <td>
                    <div>
                      <strong>${center.budget_amount?.toLocaleString() || '0'}</strong>
                      <small className="text-muted d-block">
                        {center.actual_amount > 0 && center.budget_amount > 0 
                          ? `${((center.actual_amount / center.budget_amount) * 100).toFixed(1)}%`
                          : '0%'
                        } مستغل
                      </small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <strong>${center.actual_amount?.toLocaleString() || '0'}</strong>
                      <small className="text-muted d-block">
                        ${(center.budget_amount - center.actual_amount)?.toLocaleString() || '0'} متبقي
                      </small>
                    </div>
                  </td>
                  <td>{getStatusBadge(center.status)}</td>
                  <td>{getBudgetStatus(center)}</td>
                  <td>
                    <div className="btn-group" role="group">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/cost-centers/view/${center.id}`)}
                        title={t('accounting.costCenters.actions.view')}
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => navigate(`/cost-centers/edit/${center.id}`)}
                        title={t('accounting.costCenters.actions.edit')}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => handleToggleStatus(center.id)}
                        title={t('accounting.costCenters.actions.toggleStatus')}
                      >
                        <i className={`fas fa-${center.status === 'active' ? 'pause' : 'play'}`}></i>
                      </button>
                      {!center.is_default && (
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => handleSetDefault(center.id)}
                          title={t('accounting.costCenters.actions.setDefault')}
                        >
                          <i className="fas fa-star"></i>
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(center.id)}
                        title={t('accounting.costCenters.actions.delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCenters.length === 0 && (
          <div className="text-center py-4">
            <p className="text-muted">لا توجد مراكز تكلفة لعرضها</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostCenters;
